package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.model.AttentionItem
import com.dotdrives.model.AttentionKind
import com.dotdrives.model.Donation
import com.dotdrives.model.DonationSmartDefaults
import com.dotdrives.model.GivingStatement
import com.dotdrives.model.GivingStatementBatchSummary
import com.dotdrives.model.GivingStatementDraft
import com.dotdrives.model.NewDonation
import com.dotdrives.model.Pledge
import com.dotdrives.model.Target
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.FilterOperator
import kotlinx.datetime.LocalDate
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlin.time.Clock
import kotlin.time.Instant

class DonationClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val authClient: AuthClient = AuthClient(client),
) {
    suspend fun targets(orgId: String): List<Target> =
        client.from(TARGETS_TABLE)
            .select {
                order("name", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("is_active", true)
                }
            }
            .decodeList()

    /**
     * `occurredFrom`/`occurredTo` are inclusive. `query` matches giver display name, memo, or
     * target name — since giver/target names only exist after the enrichment join below, the
     * match happens client-side against the enriched rows rather than as a server-side
     * `ilike`, the same tradeoff `BOARD_LIMIT` already makes in DriveClient.
     */
    suspend fun list(
        orgId: String,
        occurredFrom: Instant,
        occurredTo: Instant,
        query: String?,
        targetId: String?,
        source: String?,
    ): List<Donation> {
        // PostgREST caps every response at config.toml's max_rows (1000) regardless of any
        // limit() requested here -- a single select silently truncates once a month crosses
        // that many gifts. Page with range() until a short page proves there is no more.
        // occurred_at DESCENDING alone is not a unique sort -- ties straddling a page boundary
        // can duplicate or drop a row across two separate offset queries, since Postgres does
        // not guarantee stable order for an unordered-beyond-that tie. `id` is the tiebreaker
        // that makes the sort a total order.
        val rows = fetchAllPages(PAGE_SIZE) { offset ->
            client.from(DONATIONS_TABLE)
                .select {
                    range(offset, offset + PAGE_SIZE - 1)
                    order("occurred_at", Order.DESCENDING)
                    order("id", Order.ASCENDING)
                    filter {
                        eq("org_id", orgId)
                        gte("occurred_at", occurredFrom.toString())
                        lte("occurred_at", occurredTo.toString())
                        targetId?.let { eq("target_id", it) }
                        source?.let { eq("source", it) }
                    }
                }
                .decodeList<DonationRow>()
        }
        val enriched = enrich(orgId, rows)
        val trimmed = query?.trim().orEmpty()
        return if (trimmed.isEmpty()) {
            enriched
        } else {
            enriched.filter { donation ->
                donation.giverDisplayName.contains(trimmed, ignoreCase = true) ||
                    donation.memo.contains(trimmed, ignoreCase = true) ||
                    donation.target.orEmpty().contains(trimmed, ignoreCase = true)
            }
        }
    }

    suspend fun get(orgId: String, donationId: String): Donation {
        val row = client.from(DONATIONS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", donationId)
                }
            }
            .decodeSingle<DonationRow>()
        return enrich(orgId, listOf(row)).single()
    }

    /**
     * Both real needs-attention categories, combined — see [AttentionItem]'s doc for why a
     * duplicate-giver category is not included. `asOf` is compared against `expected_by`
     * (a `date` column) by its own date portion.
     */
    suspend fun attentionQueue(orgId: String, asOf: Instant): List<AttentionItem> {
        val syncErrorRows = client.from(DONATIONS_TABLE)
            .select {
                order("recorded_at", Order.DESCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("legacy_is_active", true)
                    filterNot("qbo_sync_error", FilterOperator.IS, null)
                }
            }
            .decodeList<DonationRow>()
        val syncErrors = enrich(orgId, syncErrorRows)
            .map { AttentionItem(AttentionKind.QboSyncError, donation = it) }

        val asOfDate = asOf.toString().substringBefore('T')
        val unpaidRows = client.from(PLEDGES_TABLE)
            .select {
                order("expected_by", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    filter("fulfilled_at", FilterOperator.IS, null)
                    lt("expected_by", asOfDate)
                }
            }
            .decodeList<PledgeRow>()
        val unpaid = enrichPledges(orgId, unpaidRows)
            .map { AttentionItem(AttentionKind.UnpaidPledge, pledge = it) }

        return syncErrors + unpaid
    }

    suspend fun smartDefaults(orgId: String, giverId: String): DonationSmartDefaults {
        val lastDonation = client.from(DONATIONS_TABLE)
            .select {
                order("occurred_at", Order.DESCENDING)
                limit(1)
                filter {
                    eq("org_id", orgId)
                    eq("giver_id", giverId)
                    eq("legacy_is_active", true)
                }
            }
            .decodeList<DonationRow>()
            .firstOrNull()

        val activeDrive = client.from(DRIVES_TABLE)
            .select {
                order("created_at", Order.DESCENDING)
                limit(1)
                filter {
                    eq("org_id", orgId)
                    eq("giver_id", giverId)
                    filter("archived_at", FilterOperator.IS, null)
                    filter("outcome", FilterOperator.IS, null)
                }
            }
            .decodeList<DonationDriveLookupRow>()
            .firstOrNull()

        val openPledge = activeDrive?.let { drive ->
            client.from(PLEDGES_TABLE)
                .select {
                    order("occurred_at", Order.DESCENDING)
                    limit(1)
                    filter {
                        eq("org_id", orgId)
                        eq("giver_id", giverId)
                        eq("drive_id", drive.id)
                        filter("fulfilled_at", FilterOperator.IS, null)
                    }
                }
                .decodeList<PledgeRow>()
                .firstOrNull()
        }

        val targetId = lastDonation?.targetId
        val targetName = targetId?.let { loadTargetNames(orgId, listOf(it))[it] }

        return DonationSmartDefaults(
            targetId = targetId,
            targetName = targetName,
            paymentType = lastDonation?.paymentType,
            driveId = activeDrive?.id,
            driveTitle = activeDrive?.title,
            pledgeId = openPledge?.id,
            pledgeAmount = openPledge?.amount,
        )
    }

    suspend fun record(orgId: String, donation: NewDonation): Donation {
        val id = uuidV7()
        client.from(DONATIONS_TABLE).insert(
            DonationInsert(
                id = id,
                orgId = orgId,
                giverId = donation.giverId,
                driveId = donation.driveId,
                pledgeId = donation.pledgeId,
                targetId = donation.targetId,
                amount = donation.amount,
                feeAmount = donation.feeAmount,
                coveredByGiver = donation.coveredByGiver,
                paymentType = donation.paymentType,
                source = donation.source,
                memo = donation.memo,
                occurredAt = donation.occurredAt,
                reversesDonationId = donation.reversesDonationId,
                createdBy = authClient.requireCurrentUserId(),
            ),
        )
        return get(orgId, id)
    }

    suspend fun statementBatchSummary(orgId: String, year: Int): GivingStatementBatchSummary {
        val from = Instant.parse("$year-01-01T00:00:00Z")
        val to = Instant.parse("${year + 1}-01-01T00:00:00Z")
        // The design's own year-end frame states 1,038 gifts for 412 givers -- above
        // PostgREST's max_rows (1000) cap. A truncated select here writes an understated,
        // permanently uncorrectable total_amount into giving_statements (insert-only, no
        // update grant), so this has to page in full. Ordered by `id` explicitly (this select
        // has no other sort key at all) -- offset pagination without any order() has no
        // guaranteed row order across two separate queries and can duplicate or drop a row.
        val rows = fetchAllPages(PAGE_SIZE) { offset ->
            client.from(DONATIONS_TABLE)
                .select {
                    range(offset, offset + PAGE_SIZE - 1)
                    order("id", Order.ASCENDING)
                    filter {
                        eq("org_id", orgId)
                        gte("occurred_at", from.toString())
                        lt("occurred_at", to.toString())
                    }
                }
                .decodeList<StatementDonationRow>()
        }

        val byGiver = rows.filter(StatementDonationRow::isActive).groupBy(StatementDonationRow::giverId)
        val giverNames = loadGiverNames(orgId, byGiver.keys.toList())
        val drafts = byGiver.mapNotNull { (giverId, giftRows) ->
            val total = giftRows.sumOf(StatementDonationRow::amount)
            if (total <= 0.0) return@mapNotNull null
            GivingStatementDraft(
                giverId = giverId,
                giverDisplayName = giverNames[giverId] ?: "Unknown giver",
                totalAmount = total,
                giftCount = giftRows.count { it.amount > 0.0 },
            )
        }.sortedByDescending(GivingStatementDraft::totalAmount)

        val existing = fetchAllPages(PAGE_SIZE) { offset ->
            client.from(GIVING_STATEMENTS_TABLE).select {
                range(offset, offset + PAGE_SIZE - 1)
                order("id", Order.ASCENDING)
                filter { eq("org_id", orgId); eq("year", year) }
            }.decodeList<GivingStatementStoredRow>()
        }

        return GivingStatementBatchSummary(year = year, drafts = drafts, alreadyGeneratedGiverIds = existing.map { it.giverId }.toSet(),
            statements = existing.map { row -> GivingStatement(row.id,row.orgId,row.giverId,giverNames[row.giverId] ?: "Saved statement",row.year,row.totalAmount,row.giftCount,row.pdfStoragePath,row.generatedAt,row.emailedAt,row.mailedAt) })
    }

    /** Server snapshots and deduplicates each org/giver/year atomically, then queues email.
     * A concurrent batch or a partially completed retry returns the original immutable row. */
    suspend fun generateStatements(
        orgId: String,
        year: Int,
        drafts: List<GivingStatementDraft>,
    ): List<GivingStatement> = drafts.distinctBy(GivingStatementDraft::giverId).map { draft ->
        val id = client.postgrest.rpc("request_giving_statement", buildJsonObject {
            put("p_org", orgId); put("p_giver", draft.giverId); put("p_year", year)
        }).decodeAs<String>()
        val row = client.from(GIVING_STATEMENTS_TABLE).select {
            filter { eq("org_id", orgId); eq("id", id) }
        }.decodeList<GivingStatementStoredRow>().single()
        GivingStatement(row.id, row.orgId, row.giverId, draft.giverDisplayName, row.year,
            row.totalAmount, row.giftCount, row.pdfStoragePath, row.generatedAt, row.emailedAt, row.mailedAt)
    }

    private suspend fun enrich(orgId: String, rows: List<DonationRow>): List<Donation> {
        if (rows.isEmpty()) return emptyList()
        val giverNames = loadGiverNames(orgId, rows.map(DonationRow::giverId).distinct())
        val targetNames = loadTargetNames(orgId, rows.mapNotNull(DonationRow::targetId).distinct())
        val driveTitles = loadDriveTitles(orgId, rows.mapNotNull(DonationRow::driveId).distinct())
        val stewardNames = loadUserNames(rows.mapNotNull(DonationRow::stewardUserId).distinct())
        val statuses = rows.chunked(1000).flatMap { chunk ->
            client.postgrest.rpc("donation_workflow_status", buildJsonObject {
                put("p_org", orgId); put("p_ids", kotlinx.serialization.json.JsonArray(chunk.map { kotlinx.serialization.json.JsonPrimitive(it.id) }))
            }).decodeAs<List<DonationWorkflowRow>>()
        }.associate { it.id to it.status }
        return rows.map { row ->
            Donation(
                id = row.id,
                orgId = row.orgId,
                giverId = row.giverId,
                giverDisplayName = giverNames[row.giverId] ?: "Unknown giver",
                driveId = row.driveId,
                driveTitle = row.driveId?.let(driveTitles::get),
                pledgeId = row.pledgeId,
                targetId = row.targetId,
                targetName = row.targetId?.let(targetNames::get),
                stewardUserId = row.stewardUserId,
                stewardName = row.stewardUserId?.let(stewardNames::get),
                amount = row.amount,
                feeAmount = row.feeAmount,
                coveredByGiver = row.coveredByGiver,
                paymentType = row.paymentType,
                source = row.source,
                memo = row.memo,
                occurredAt = row.occurredAt,
                recordedAt = row.recordedAt,
                reversesDonationId = row.reversesDonationId,
                receiptSentAt = row.receiptSentAt,
                qboSyncedAt = row.qboSyncedAt,
                qboSyncError = row.qboSyncError?.let { "QuickBooks could not sync this gift. An administrator can review the connection and retry." },
                qboWorkflowStatus = statuses[row.id],
                createdBy = row.createdBy,
                isActive = row.isActive,
            )
        }
    }

    private suspend fun enrichPledges(orgId: String, rows: List<PledgeRow>): List<Pledge> {
        if (rows.isEmpty()) return emptyList()
        val giverNames = loadGiverNames(orgId, rows.map(PledgeRow::giverId).distinct())
        val targetNames = loadTargetNames(orgId, rows.mapNotNull(PledgeRow::targetId).distinct())
        val driveTitles = loadDriveTitles(orgId, rows.mapNotNull(PledgeRow::driveId).distinct())
        return rows.map { row ->
            Pledge(
                id = row.id,
                orgId = row.orgId,
                giverId = row.giverId,
                giverDisplayName = giverNames[row.giverId] ?: "Unknown giver",
                driveId = row.driveId,
                driveTitle = row.driveId?.let(driveTitles::get),
                targetId = row.targetId,
                targetName = row.targetId?.let(targetNames::get),
                amount = row.amount,
                expectedBy = row.expectedBy,
                fulfilledAt = row.fulfilledAt,
                fulfillingDonationId = row.fulfillingDonationId,
                occurredAt = row.occurredAt,
                createdBy = row.createdBy,
            )
        }
    }

    private suspend fun loadGiverNames(orgId: String, giverIds: List<String>): Map<String, String> {
        if (giverIds.isEmpty()) return emptyMap()
        return client.from(GIVERS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    isIn("id", giverIds)
                }
            }
            .decodeList<DonationGiverLookupRow>()
            .associate { it.id to it.displayName }
    }

    private suspend fun loadTargetNames(orgId: String, targetIds: List<String>): Map<String, String> {
        if (targetIds.isEmpty()) return emptyMap()
        return client.from(TARGETS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    isIn("id", targetIds)
                }
            }
            .decodeList<DonationTargetNameRow>()
            .associate { it.id to it.name }
    }

    private suspend fun loadDriveTitles(orgId: String, driveIds: List<String>): Map<String, String> {
        if (driveIds.isEmpty()) return emptyMap()
        return client.from(DRIVES_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    isIn("id", driveIds)
                }
            }
            .decodeList<DonationDriveLookupRow>()
            .associate { it.id to it.title }
    }

    private suspend fun loadUserNames(userIds: List<String>): Map<String, String> {
        if (userIds.isEmpty()) return emptyMap()
        return client.from(PROFILES_TABLE)
            .select {
                filter {
                    isIn("user_id", userIds)
                }
            }
            .decodeList<DonationProfileRow>()
            .associate { profile ->
                profile.userId to listOf(profile.firstName, profile.lastName)
                    .filter(String::isNotBlank)
                    .joinToString(" ")
            }
    }
}

@Serializable
private data class DonationRow(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("giver_id")
    val giverId: String,
    @SerialName("drive_id")
    val driveId: String?,
    @SerialName("pledge_id")
    val pledgeId: String?,
    @SerialName("target_id")
    val targetId: String?,
    @SerialName("steward_user_id")
    val stewardUserId: String?,
    val amount: Double,
    @SerialName("fee_amount")
    val feeAmount: Double,
    @SerialName("covered_by_giver")
    val coveredByGiver: Boolean,
    @SerialName("payment_type")
    val paymentType: String,
    val source: String,
    val memo: String,
    @SerialName("occurred_at")
    val occurredAt: Instant,
    @SerialName("recorded_at")
    val recordedAt: Instant,
    @SerialName("reverses_donation_id")
    val reversesDonationId: String?,
    @SerialName("receipt_sent_at")
    val receiptSentAt: Instant?,
    @SerialName("qbo_synced_at")
    val qboSyncedAt: Instant?,
    @SerialName("qbo_sync_error")
    val qboSyncError: String?,
    @SerialName("created_by")
    val createdBy: String?,
    @SerialName("legacy_is_active")
    val isActive: Boolean = true,
)

@Serializable
private data class DonationInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("giver_id")
    val giverId: String,
    @SerialName("drive_id")
    val driveId: String?,
    @SerialName("pledge_id")
    val pledgeId: String?,
    @SerialName("target_id")
    val targetId: String?,
    val amount: Double,
    @SerialName("fee_amount")
    val feeAmount: Double,
    @SerialName("covered_by_giver")
    val coveredByGiver: Boolean,
    @SerialName("payment_type")
    val paymentType: String,
    val source: String,
    val memo: String,
    @SerialName("occurred_at")
    val occurredAt: Instant,
    @SerialName("reverses_donation_id")
    val reversesDonationId: String?,
    @SerialName("created_by")
    val createdBy: String,
)

@Serializable
private data class PledgeRow(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("giver_id")
    val giverId: String,
    @SerialName("drive_id")
    val driveId: String?,
    @SerialName("target_id")
    val targetId: String?,
    val amount: Double,
    @SerialName("expected_by")
    val expectedBy: LocalDate?,
    @SerialName("fulfilled_at")
    val fulfilledAt: Instant?,
    @SerialName("fulfilling_donation_id")
    val fulfillingDonationId: String?,
    @SerialName("occurred_at")
    val occurredAt: Instant,
    @SerialName("created_by")
    val createdBy: String,
)

@Serializable
private data class StatementDonationRow(
    val id: String,
    @SerialName("giver_id")
    val giverId: String,
    val amount: Double,
    @SerialName("legacy_is_active")
    val isActive: Boolean = true,
)

@Serializable
private data class GivingStatementExistsRow(
    @SerialName("giver_id")
    val giverId: String,
)

@Serializable
private data class GivingStatementStoredRow(
    val id: String,
    @SerialName("org_id") val orgId: String,
    @SerialName("giver_id") val giverId: String,
    val year: Int,
    @SerialName("total_amount") val totalAmount: Double,
    @SerialName("gift_count") val giftCount: Int,
    @SerialName("pdf_storage_path") val pdfStoragePath: String? = null,
    @SerialName("generated_at") val generatedAt: Instant? = null,
    @SerialName("emailed_at") val emailedAt: Instant? = null,
    @SerialName("mailed_at") val mailedAt: Instant? = null,
)

@Serializable
private data class DonationGiverLookupRow(
    val id: String,
    @SerialName("display_name")
    val displayName: String,
)

@Serializable
private data class DonationTargetNameRow(
    val id: String,
    val name: String,
)

@Serializable
private data class DonationDriveLookupRow(
    val id: String,
    val title: String,
)

@Serializable
private data class DonationProfileRow(
    @SerialName("user_id")
    val userId: String,
    @SerialName("first_name")
    val firstName: String,
    @SerialName("last_name")
    val lastName: String,
)

private const val DONATIONS_TABLE = "donations"
private const val PLEDGES_TABLE = "pledges"
private const val TARGETS_TABLE = "targets"
private const val DRIVES_TABLE = "drives"
private const val GIVERS_TABLE = "givers"
private const val PROFILES_TABLE = "profiles"
private const val GIVING_STATEMENTS_TABLE = "giving_statements"
private const val PAGE_SIZE = 1_000L

@kotlinx.serialization.Serializable
private data class DonationWorkflowRow(val id: String, val status: String)
